import store from "@/store";
import { endorsementTypes } from "@/constants/endorsements";
import { partTypeStrings } from "@/constants/part-type-strings";
import { storeActions } from "@/constants/store-actions";
import { hasEndorsementType } from "@/helpers/endorsements-helper";
import { isUsableVin } from "@/helpers/policy-vehicle-helper";
import {
    callRevisitPartSelection,
    handleRevisitPartSelectionResponse,
} from "@/helpers/revisit-part-selection-helper";

function getWindshieldLineItem(partsList = []) {
    return partsList.find((part) => part.partType === partTypeStrings.WINDSHIELD);
}

function shouldSkipPartsCall({ vinAlreadyCollected } = {}) {
    if (vinAlreadyCollected) return true;
    if (!isUsableVin(store.getters.order?.vehicle?.vin)) return true;
    if (!getWindshieldLineItem(store.getters.order?.lineItems?.glassParts ?? [])) return true;
    return false;
}

function toGlassPartLineItem(part) {
    return {
        partNumber: part.partNumber,
        basePartNumber: part.basePartNumber,
        description: part.description,
        color: part.color,
        partType: part.partType,
        canSafeliteRecalibrate: part.canSafeliteRecalibrate,
        requiresRecalibration: part.requiresRecalibration,
        requiresCapabilityQuestions: part.requiresCapabilityQuestions,
        recalibrationType: part.recalibrationType,
        childParts: part.childParts,
        kitPrice: part.kitPrice,
        sellingPrice: part.sellingPrice,
        laborAmount: part.laborAmount,
    };
}

function flattenParts(glassPieceParts = []) {
    return glassPieceParts.flatMap((glass) => glass.parts ?? []);
}

export function getSpsPartsToSwap(response) {
    if (!response) return null;

    const spsParts = flattenParts(response.glassPieceParts).filter(
        (part) => part.isSpsPart === true
    );

    if (spsParts.length !== 1) return null;
    if (spsParts[0].partType !== partTypeStrings.WINDSHIELD) return null;
    return spsParts[0];
}

export function isOemPartNumber(partNumber) {
    return typeof partNumber === "string" && partNumber.endsWith("OEM");
}

export function getReturnedWindshieldPartNumber(response) {
    const spsPart = getSpsPartsToSwap(response);
    if (spsPart?.partNumber) {
        return spsPart.partNumber;
    }

    return flattenParts(response?.glassPieceParts).find(
        (part) => part.partType === partTypeStrings.WINDSHIELD
    )?.partNumber;
}

export async function callPartsForPolicyVehicleVin({
    pageNameToLog,
    vin,
    oemEndorsementFlag,
} = {}) {
    const response = await store.dispatch(storeActions.GET_PARTS, {
        pageNameToLog,
        ...(vin ? { vin } : {}),
        ...(oemEndorsementFlag !== undefined ? { oemEndorsementFlag } : {}),
    });

    return response?.data;
}

export async function swapWindshieldIfSingleDifferentSpsPart(response) {
    const existingParts = store.getters.order?.lineItems?.glassParts ?? [];
    const existingWindshield = getWindshieldLineItem(existingParts);
    const spsWindshield = getSpsPartsToSwap(response);

    if (!existingWindshield || !spsWindshield) return false;
    if (spsWindshield.partNumber === existingWindshield.partNumber) return false;

    const swappedParts = existingParts.map((part) =>
        part.partType === partTypeStrings.WINDSHIELD ? toGlassPartLineItem(spsWindshield) : part
    );

    await store.dispatch(storeActions.SAVE_GLASS_PARTS, swappedParts);
    return true;
}

export async function requestAccuratePartForPolicyVehicleVin({
    vinAlreadyCollected,
    pageNameToLog,
} = {}) {
    if (shouldSkipPartsCall({ vinAlreadyCollected })) return;

    try {
        const response = await callPartsForPolicyVehicleVin({ pageNameToLog });
        await swapWindshieldIfSingleDifferentSpsPart(response);
    } catch {
        // VIN part swap must not block C&C navigation
    }
}

export async function requestPartsAndOemRevisitForSelectedVehicle({
    selectedPolicyVehicle,
    pageNameToLog,
    shouldApply = () => true,
} = {}) {
    if (store.getters.order?.damage?.isRepair === true) return;
    if (!isUsableVin(selectedPolicyVehicle?.vin)) return;
    const isOemEndorsed = hasEndorsementType(
        selectedPolicyVehicle?.endorsements,
        endorsementTypes.OEM_APPROVED
    );
    try {
        const response = await callPartsForPolicyVehicleVin({
            pageNameToLog,
            vin: selectedPolicyVehicle.vin,
            oemEndorsementFlag: isOemEndorsed,
        });

        if (!shouldApply()) return;

        await swapWindshieldIfSingleDifferentSpsPart(response);

        const partNumber = getReturnedWindshieldPartNumber(response);
        if (!partNumber || isOemPartNumber(partNumber)) return;

        if (
            !hasEndorsementType(selectedPolicyVehicle?.endorsements, endorsementTypes.OEM_APPROVED)
        ) {
            return;
        }

        const requestPartsList = store.getters.order?.lineItems?.glassParts ?? [];
        if (requestPartsList.length === 0) return;

        const revisitResponse = await callRevisitPartSelection({
            pageNameToLog,
            isOemEndorsed: isOemEndorsed,
        });

        if (!shouldApply()) return;

        return handleRevisitPartSelectionResponse(revisitResponse, requestPartsList);
    } catch {
        // VIN part lookup and OEM revisit must not block C&C navigation
    }
}
